import { db, doc, getDoc, setDoc, handleFirestoreError, OperationType } from '@/firebase';

export interface LegalDocument {
  version: string;
  effectiveDate: string;
  content: string;
  updatedAt: number;
}

export interface UserAcceptance {
  version: string;
  timestamp: number;
  ipHash: string;
}

// Simple IP hashing helper using Web Crypto API
export async function generateIpHash(ip: string): Promise<string> {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(ip || 'unknown_ip');
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (err) {
    console.warn('Crypto subystem not available or failed', err);
    return 'fingerprint_' + Math.random().toString(36).substring(2, 15);
  }
}

// Fetch client public IP for compliance purposes
export async function fetchClientIp(): Promise<string> {
  try {
    const res = await fetch('https://api.ipify.org?format=json', { timeout: 3000 } as any);
    const data = await res.json();
    return data.ip || '127.0.0.1';
  } catch (err) {
    return 'client_fingerprint_' + navigator.userAgent;
  }
}

// Initial markdown text from specifications
export const INITIAL_TERMS = `**TERMS OF SERVICE**

**eNvesta AI Terms of Service**

Effective Date: 2026, June, 15th

**1. Acceptance of Terms**

By accessing or using eNvesta AI, you agree to be bound by these Terms of Service. If you do not agree, do not use the platform. Continued use after modifications constitutes acceptance.

**2. Nature of Service**

eNvesta AI is a business intelligence and market research platform. We aggregate public data, apply algorithmic analysis, and present structured information to assist entrepreneurs in business planning. We are not a financial advisor, investment adviser, broker-dealer, registered representative, valuation professional, or licensed appraiser.

**3. Not Financial Advice**

No output from eNvesta AI constitutes financial, investment, legal, tax, or business advice. All scores, projections, estimates, benchmarks, and recommendations are algorithmic outputs based on aggregated data and user inputs. They do not account for your specific circumstances, risk tolerance, or professional needs.

**4. No Fiduciary Relationship**

Use of eNvesta AI does not create an advisory, fiduciary, or client relationship. We owe no duty of care or loyalty regarding your financial or business decisions. You are solely responsible for your choices and their consequences.

**5. No Securities Activity**

eNvesta AI does not recommend the purchase or sale of securities, facilitate investment transactions, manage investment portfolios, provide investment research for compensation, or act as a broker, dealer, or investment intermediary. Our funding landscape feature aggregates publicly available information about entities that have disclosed investment activity. We do not verify accuracy, current availability, or suitability. We receive no compensation for introductions and make none.

**6. No Valuation Opinions**

eNvesta AI does not provide business valuations. Our market comparable research presents disclosed transaction ranges from public records. These are not appraisals, fair market value opinions, or indications of value. For actual valuation, engage a licensed appraiser or valuation professional.

**7. User Acknowledgment**

Before accessing paid features, you must acknowledge that you understand eNvesta AI is not a financial advisor, that you will consult qualified professionals before committing capital, that all projections are estimates and not guarantees, and that you accept full responsibility for your business decisions. This acknowledgment is recorded and stored.

**8. Limitation of Liability**

eNvesta AI is not liable for business losses based on platform outputs, investment losses from third-party funding relationships, regulatory penalties from business activities, or damages from reliance on algorithmic estimates. Our maximum liability is limited to fees paid in the twelve months preceding the claim.

**9. Jurisdictional Compliance**

Users in regulated markets are responsible for ensuring their use of eNvesta AI complies with local securities, financial services, and consumer protection laws. eNvesta AI makes no representation that the platform is lawful in all jurisdictions.

**10. Modifications**

We may modify these terms at any time. Continued use after modification constitutes acceptance. Material changes will be notified via email or in-app notice.

**11. Governing Law & Arbitration**

These terms are governed by the laws of Uganda. Any dispute, claim, or controversy arising out of or relating to your use of eNvesta AI shall be resolved through binding arbitration as set forth in the [Arbitration Agreement](/legal/arbitration) which is incorporated herein by reference. If any provision is unenforceable, the remainder remains in effect.

**12. Contact**

For questions about these terms, contact envestaai@gmail.com.`;

export const INITIAL_PRIVACY = `**PRIVACY POLICY**

**eNvesta AI Privacy Policy**

Effective Date: 2026, June, 15th

**1. Data We Collect**

We collect account information (name, email, phone), business inputs (venture descriptions, locations, financial estimates), location data (coordinates, addresses), device information (model, OS, IP address), usage analytics (feature usage, session duration), and payment details (processed by Dodo Payments, not stored by us).

**2. How We Use Data**

We use data to provide and improve the service, process AI-generated insights, personalize your experience, ensure security, and comply with legal obligations. We do not sell your data to third parties.

**3. Data Sharing**

We share data with service providers including Firebase (hosting), Google Cloud (AI processing), Gemini API (analysis), and Dodo Payments (billing). All processors are contractually bound to data protection standards. We do not share with advertisers or data brokers.

**4. Data Storage and Retention**

Data is stored in Firebase Firestore with regional hosting. Analytics retained 90 days. Account data retained until deletion. Deleted accounts enter 30-day purge window before permanent removal. Backups retained 30 days.

**5. Your Rights**

You may access, correct, or delete your data. You may export your data in standard format. You may object to certain processing. Contact eNvestaai@gmail.com to exercise rights.

**6. Cookies and Tracking**

We use essential cookies for functionality, functional cookies for preferences, and analytics cookies for usage understanding. You may manage preferences in settings. Analytics respects do-not-track signals.

**7. International Transfers**

Data may process in the United States, European Union, or African regions depending on service provider infrastructure. We apply appropriate safeguards for cross-border transfers.

**8. Children's Privacy**

eNvesta AI is not intended for users under 18. We do not knowingly collect data from children.

**9. Security**

We encrypt data in transit and at rest. We use industry-standard security practices. No system is completely secure. Report vulnerabilities to eNvestaai@gmail.com.

**10. Changes**

We may update this policy. Material changes will be notified. Continued use constitutes acceptance.

**11. Contact**

For privacy questions, contact eNvestaai@gmail.com.`;

export const INITIAL_ARBITRATION = `**ARBITRATION AGREEMENT**

**eNvesta AI Arbitration Agreement**

Effective Date: 2026, June, 15th

This Arbitration Agreement is incorporated by reference into the eNvesta AI Terms of Service. By using eNvesta AI, you agree to the terms below.

**1. Purpose**

Any dispute, claim, or controversy arising out of or relating to your use of eNvesta AI, including but not limited to the Terms of Service, Privacy Policy, or any service provided, shall be resolved through binding arbitration as described herein, except where prohibited by applicable law.

**2. Agreement to Arbitrate**

You and eNvesta AI mutually agree to waive the right to a trial by jury or to participate in a class action. All disputes shall be resolved through individual arbitration.

**3. Arbitration Rules**

Arbitration shall be conducted under the rules of the Nairobi Centre for International Arbitration (NCIA) for users in Africa, or the London Court of International Arbitration (LCIA) for users outside Africa, at eVesta's election. The seat of arbitration shall be Kampala, Uganda, or London, England, corresponding to the chosen institution.

**4. Language**

The language of arbitration shall be English.

**5. Number of Arbitrators**

One arbitrator shall preside, unless the claim exceeds USD 100,000, in which case three arbitrators shall preside.

**6. Costs**

Each party shall bear its own costs. eNvesta AI shall advance the arbitrator's fees and recover them from the prevailing party or as directed in the award.

**7. Confidentiality**

Arbitration proceedings, including the existence of a dispute, shall remain confidential, except as required to enforce the award.

**8. Enforceability**

The award is final and binding. Judgment may be entered in any court of competent jurisdiction.

**9. Exceptions**

This agreement does not prevent either party from seeking injunctive relief in a court of competent jurisdiction to prevent irreparable harm.

**10. Severability**
 
If any provision is unenforceable, the remainder remains in effect.`;

export const INITIAL_COOKIE_POLICY = `**COOKIE POLICY**

**eNvesta AI Cookies Policy**

Effective Date: June 15, 2026
Last Updated: June 15, 2026

**1. Introduction**

This Cookies Policy explains how eNvesta AI (“we,” “us,” or “our”) uses cookies and similar tracking technologies when you access our platform via web (envestaai.com) or our Mobile Devices applications.
By using eNvesta AI, you consent to the use of cookies as described in this policy. Your consent may be explicit (via our cookie banner for certain regions) or implied (by continued use of the platform where a banner is not presented).

**2. What Are Cookies?**

Cookies are small text files stored on your device (computer, tablet, or mobile) when you visit a website or use an application. They help us recognize your device, remember your preferences, and improve your experience.
We also use similar technologies such as:
- LocalStorage and SessionStorage — for temporary data storage in your browser
- Web beacons — tiny graphics files for analytics
- Device identifiers — for our Android application

**3. How We Use Cookies**

eNvesta uses cookies and similar technologies for the following purposes:

**3.1 Essential Cookies (Strictly Necessary)**

These cookies are required for the platform to function. Without them, core features will not work. You cannot opt out of these cookies.

| Cookie/Source | Purpose | Duration |
| :--- | :--- | :--- |
| Firebase Authentication | Maintains your login session and security state | Session / Up to 2 weeks if “Remember me” is selected |
| Firebase App Check | Verifies legitimate app requests and prevents abuse | Session |
| Dodo Payments (Checkout) | Processes subscription payments and mobile money securely | Session |
| CSRF Protection | Prevents cross-site request forgery attacks | Session |

**3.2 Functional Cookies**

These cookies remember your preferences and settings to enhance your experience.

| Cookie/Source | Purpose | Duration |
| :--- | :--- | :--- |
| eNvesta AI User Preferences | Remembers your dashboard layout, filters, and display settings | 1 year |
| Recent Scans State | Temporarily stores your recent reconnaissance activity in-session | Session |
| Language/Region | Remembers your selected region (Uganda, East Africa, Diaspora) | 1 year |
| Theme/Dark Mode | Remembers your visual preference | 1 year |

**3.3 Analytics Cookies**

These cookies help us understand how users interact with eNvesta AI so we can improve the platform.

| Cookie/Source | Purpose | Duration |
| :--- | :--- | :--- |
| Firebase Analytics | Tracks app usage, feature engagement, and crash reports | Up to 2 years |
| Google Analytics 4 (if enabled) | Measures web traffic, user journeys, and conversion funnels | Up to 2 years |

**3.4 AI & Performance Cookies**

These cookies support our AI features and platform optimization.

| Cookie/Source | Purpose | Duration |
| :--- | :--- | :--- |
| Gemini API Session | Maintains context for AI-powered reconnaissance and insights | Session |
| Performance Monitoring | Tracks load times and API response latency | 30 days |

**4. Third-Party Cookies**

eNvesta AI integrates with trusted third-party services that may set their own cookies:

| Third Party | Purpose | Privacy Policy |
| :--- | :--- | :--- |
| Google (Firebase) | Authentication, database, analytics, and app infrastructure | [Google Privacy Policy](https://policies.google.com/privacy) |
| Dodo Payments | Subscription billing and payment processing | [Dodo Payments Privacy Policy](https://dodopayments.com) |

We do not control these third-party cookies. Please review their privacy policies for more information.

**5. Cookies in Our Android Application**

Our Android app uses equivalent technologies to cookies:
- Firebase Instance ID — for push notifications and app analytics
- Android SharedPreferences — for storing your login state and preferences
- Secure Storage — for sensitive data like authentication tokens

These are governed by the same principles as web cookies and are essential for app functionality.

**6. Regional Cookie Consent**

**6.1 European Economic Area (EEA) and United Kingdom**

Users accessing eNvesta AI from the EEA or UK are presented with a cookie consent banner on their first visit. This banner allows you to:
- Accept All — Enable essential, functional, analytics, and AI performance cookies
- Essential Only — Enable only strictly necessary cookies
- Customize — Open a detailed settings panel to toggle individual cookie categories

Your choice is stored in localStorage and respected across sessions. You can change your preferences at any time via Privacy Settings in your profile sidebar or footer.

**6.2 Uganda, East Africa, and Other Regions**

Users outside the EEA/UK are not shown a cookie consent banner. For these users, all cookies are enabled by implied consent through continued use of the platform. You may disable non-essential cookies at any time via your browser settings or our Privacy Settings page.

**6.3 How We Detect Your Region**

We detect your region using your browser’s timezone (Intl.DateTimeFormat().resolvedOptions().timeZone) to determine whether to display the consent banner. No personal data is transmitted for this check.

**7. Your Choices: Managing Cookies**

**7.1 Via Cookie Banner (EEA/UK Users)**

On your first visit, or by clicking “Manage Cookies” in the footer, you can:
- Accept all cookies
- Reject non-essential cookies
- Customize which categories you allow

**7.2 Via Privacy Settings (All Users)**

Access Privacy Settings from your profile sidebar or the footer to:
- View your current cookie preferences
- Enable or disable functional cookies
- Enable or disable analytics cookies
- Enable or disable AI performance cookies
- Export your cookie consent record
- Reset all preferences

**7.3 Via Browser Settings**

You can control cookies through your browser settings:
- Chrome: Settings → Privacy and Security → Cookies and other site data
- Firefox: Preferences → Privacy & Security → Cookies and Site Data
- Safari: Preferences → Privacy → Cookies and website data
- Edge: Settings → Cookies and site permissions

Note: Disabling essential cookies will prevent you from logging in or using core eNvesta AI features.

**7.4 Via Android App Settings**

You can manage app data through your device settings:
- Android: Settings → Apps → eNvesta → Storage → Clear Cache / Clear Data

Clearing data will log you out and reset your preferences.

**8. Data Retention**

| Cookie Category | Retention Period |
| :--- | :--- |
| Essential (Authentication) | Up to 2 weeks (or until logout) |
| Functional | 1 year |
| Analytics | Up to 2 years (or until opt-out) |
| AI Session | Duration of active session |
| Payment Session | Duration of transaction + 90 days for compliance |
| Consent Record | 2 years from date of consent |

**9. Legal Basis and Compliance**

**Uganda & East Africa**

We comply with the Data Protection and Privacy Act, 2019 (Uganda) and equivalent East African data protection frameworks. Under these frameworks, implied consent is sufficient for non-sensitive cookies used in the ordinary course of service provision.

**European Economic Area (EEA) and United Kingdom**

For users accessing eNvesta AI from the EEA or UK, we comply with the General Data Protection Regulation (GDPR) and UK GDPR:
- We request explicit consent for non-essential cookies via our banner
- You can withdraw consent at any time via Privacy Settings
- Essential cookies are processed under legitimate interest (Article 6(1)(f) GDPR)
- Non-essential cookies are processed only with your explicit consent (Article 6(1)(a) GDPR)
- Your consent is freely given, specific, informed, and unambiguous

**Other Jurisdictions**

Users from other regions are subject to the implied consent model described in Section 6.2.

**10. Updates to This Policy**

We may update this Cookies Policy as our platform evolves. Changes will be posted on this page with an updated effective date. Significant changes will be notified via:
- Email notification to registered users
- In-app notice on next login
- Banner notification for active sessions

The “Last Updated” date at the top of this policy indicates when the most recent changes were made.

**11. Contact Us**

If you have questions about this Cookies Policy, how we use cookies, or wish to exercise your data protection rights:
Email: privacy@eNvestaai.com
Data Protection Officer: dpo@eNvestaai.com
Business Address: [Your registered business address]

We aim to respond to all inquiries within 72 hours.

**12. Related Policies**

- Privacy Policy — How we collect, use, and protect your personal data
- Terms of Service — Rules for using eNvesta AI
- [Data Processing Agreement](/legal/dpa) — For enterprise clients

By continuing to use eNvesta AI, you acknowledge that you have read and understood this Cookies Policy.

eNvesta AI is a venture intelligence platform serving global markets. Built with Firebase, Gemini AI, and designed for founders, investors, and ecosystem builders.`;

/**
 * Ensures Terms of Service and Privacy Policy exist in Firestore.
 * seeds them if they are missing.
 */
export async function initializeLegalDocs(): Promise<void> {
  try {
    const termsRef = doc(db, 'config', 'legal', 'termsOfService', 'current');
    const privacyRef = doc(db, 'config', 'legal', 'privacyPolicy', 'current');
    const arbRef = doc(db, 'config', 'legal', 'arbitrationAgreement', 'current');
    const cookieRef = doc(db, 'config', 'legal', 'cookiePolicy', 'current');

    const termsSnap = await getDoc(termsRef);
    if (!termsSnap.exists()) {
      await setDoc(termsRef, {
        version: '1.1.0',
        effectiveDate: '2026, June, 15th',
        content: INITIAL_TERMS,
        updatedAt: Date.now()
      });
      console.log('Seeded Terms of Service successfully.');
    } else {
      const data = termsSnap.data();
      if (!data.content.includes('/legal/arbitration') || !data.content.includes('Arbitration Agreement')) {
        await setDoc(termsRef, {
          ...data,
          version: '1.1.0',
          content: INITIAL_TERMS,
          updatedAt: Date.now()
        }, { merge: true });
        console.log('Upgraded existing Terms of Service to include Arbitration reference.');
      }
    }

    const privacySnap = await getDoc(privacyRef);
    if (!privacySnap.exists()) {
      await setDoc(privacyRef, {
        version: '1.0.0',
        effectiveDate: '2026, June, 15th',
        content: INITIAL_PRIVACY,
        updatedAt: Date.now()
      });
      console.log('Seeded Privacy Policy successfully.');
    }

    const arbSnap = await getDoc(arbRef);
    if (!arbSnap.exists()) {
      await setDoc(arbRef, {
        version: '1.0.0',
        effectiveDate: '2026, June, 15th',
        content: INITIAL_ARBITRATION,
        updatedAt: Date.now()
      });
      console.log('Seeded Arbitration Agreement successfully.');
    }

    const cookieSnap = await getDoc(cookieRef);
    if (!cookieSnap.exists()) {
      await setDoc(cookieRef, {
        version: '1.0.0',
        effectiveDate: '2026, June, 15th',
        content: INITIAL_COOKIE_POLICY,
        updatedAt: Date.now()
      });
      console.log('Seeded Cookie Policy successfully.');
    }

    const dpaRef = doc(db, 'config', 'legal', 'dataProcessingAgreement', 'current');
    const dpaSnap = await getDoc(dpaRef);
    if (!dpaSnap.exists()) {
      await setDoc(dpaRef, {
        version: '1.0.0',
        effectiveDate: 'June 15, 2026',
        content: INITIAL_DPA_POLICY,
        updatedAt: Date.now()
      });
      console.log('Seeded Data Processing Agreement successfully.');
    }
  } catch (err: any) {
    if (err && (
      err.code === 'permission-denied' || 
      err.code === 'unavailable' ||
      err.code === 'failed-precondition' ||
      (err.message && (
        err.message.toLowerCase().includes('permission') || 
        err.message.toLowerCase().includes('offline') ||
        err.message.toLowerCase().includes('unavailable')
      ))
    )) {
      console.warn('Skipping legal document seeding (offline, unavailable, or restricted):', err.message || err);
    } else {
      console.error('Failed to initialize legal documents in Firestore:', err);
    }
  }
}

/**
 * Fetch terms from Firestore. Fallback to initial static copy if error occurs.
 */
export async function fetchTermsOfService(): Promise<LegalDocument> {
  try {
    const termsRef = doc(db, 'config', 'legal', 'termsOfService', 'current');
    const termsSnap = await getDoc(termsRef);
    if (termsSnap.exists()) {
      return termsSnap.data() as LegalDocument;
    }
  } catch (err) {
    console.warn('Error fetching Terms of Service from Firestore, falling back to static copy:', err);
  }
  return {
    version: '1.1.0',
    effectiveDate: '2026, June, 15th',
    content: INITIAL_TERMS,
    updatedAt: Date.now()
  };
}

/**
 * Fetch privacy policy from Firestore. Fallback to initial static copy if error occurs.
 */
export async function fetchPrivacyPolicy(): Promise<LegalDocument> {
  try {
    const privacyRef = doc(db, 'config', 'legal', 'privacyPolicy', 'current');
    const privacySnap = await getDoc(privacyRef);
    if (privacySnap.exists()) {
      return privacySnap.data() as LegalDocument;
    }
  } catch (err) {
    console.warn('Error fetching Privacy Policy from Firestore, falling back to static copy:', err);
  }
  return {
    version: '1.0.0',
    effectiveDate: '2026, June, 15th',
    content: INITIAL_PRIVACY,
    updatedAt: Date.now()
  };
}

/**
 * Fetch arbitration agreement from Firestore. Fallback to initial static copy if error occurs.
 */
export async function fetchArbitrationAgreement(): Promise<LegalDocument> {
  try {
    const arbRef = doc(db, 'config', 'legal', 'arbitrationAgreement', 'current');
    const arbSnap = await getDoc(arbRef);
    if (arbSnap.exists()) {
      return arbSnap.data() as LegalDocument;
    }
  } catch (err) {
    console.warn('Error fetching Arbitration Agreement from Firestore, falling back to static copy:', err);
  }
  return {
    version: '1.0.0',
    effectiveDate: '2026, June, 15th',
    content: INITIAL_ARBITRATION,
    updatedAt: Date.now()
  };
}

/**
 * Fetch cookie policy from Firestore. Fallback to initial static copy if error occurs.
 */
export async function fetchCookiePolicy(): Promise<LegalDocument> {
  try {
    const cookieRef = doc(db, 'config', 'legal', 'cookiePolicy', 'current');
    const cookieSnap = await getDoc(cookieRef);
    if (cookieSnap.exists()) {
      return cookieSnap.data() as LegalDocument;
    }
  } catch (err) {
    console.warn('Error fetching Cookie Policy from Firestore, falling back to static copy:', err);
  }
  return {
    version: '1.0.0',
    effectiveDate: '2026, June, 15th',
    content: INITIAL_COOKIE_POLICY,
    updatedAt: Date.now()
  };
}

/**
 * Fetch Data Processing Agreement from Firestore. Fallback to initial static copy if error occurs.
 */
export async function fetchDataProcessingAgreement(): Promise<LegalDocument> {
  try {
    const dpaRef = doc(db, 'config', 'legal', 'dataProcessingAgreement', 'current');
    const dpaSnap = await getDoc(dpaRef);
    if (dpaSnap.exists()) {
      return dpaSnap.data() as LegalDocument;
    }
  } catch (err) {
    console.warn('Error fetching Data Processing Agreement from Firestore, falling back to static copy:', err);
  }
  return {
    version: '1.0.0',
    effectiveDate: 'June 15, 2026',
    content: INITIAL_DPA_POLICY,
    updatedAt: Date.now()
  };
}

/**
 * Admin updates Arbitration Agreement
 */
export async function updateArbitrationAgreement(version: string, effectiveDate: string, content: string): Promise<void> {
  try {
    const arbRef = doc(db, 'config', 'legal', 'arbitrationAgreement', 'current');
    const arbData: LegalDocument = {
      version,
      effectiveDate,
      content,
      updatedAt: Date.now()
    };
    await setDoc(arbRef, arbData);
    
    // Also log historical archive
    const historyRef = doc(db, 'config', 'legal', 'arbitrationAgreement', `history_${version}`);
    await setDoc(historyRef, arbData).catch(err => console.warn('Failed to archive historical version arbitration:', err));
  } catch (err) {
    handleFirestoreError(err, OperationType.WRITE, 'config/legal/arbitrationAgreement/current');
  }
}

/**
 * Tracks click events on arbitration elements
 */
export async function trackArbitrationClick(userId?: string): Promise<void> {
  try {
    const clickRef = doc(db, 'analytics', 'arbitrationClicks');
    const clickDoc = await getDoc(clickRef);
    let clicks = 0;
    if (clickDoc.exists()) {
      clicks = clickDoc.data()?.clicks || 0;
    }
    await setDoc(clickRef, {
      clicks: clicks + 1,
      lastClickedAt: Date.now(),
      ...(userId ? { lastUser: userId } : {})
    }, { merge: true });

    // Also log to events subcollection
    const eventRef = doc(db, 'analytics', 'arbitrationClicks', 'events', `click_${Date.now()}`);
    await setDoc(eventRef, {
      timestamp: Date.now(),
      userId: userId || 'anonymous',
      userAgent: navigator.userAgent
    });
    console.log('Arbitration agreement link click logged successfully.');
  } catch (err) {
    console.warn('Failed to track arbitration click event (silently continued):', err);
  }
}

/**
 * Admin updates standard Terms of Service
 */
export async function updateTermsOfService(version: string, effectiveDate: string, content: string): Promise<void> {
  try {
    const termsRef = doc(db, 'config', 'legal', 'termsOfService', 'current');
    const termsData: LegalDocument = {
      version,
      effectiveDate,
      content,
      updatedAt: Date.now()
    };
    await setDoc(termsRef, termsData);
    
    // Also log historical archive
    const historyRef = doc(db, 'config', 'legal', 'termsOfService', `history_${version}`);
    await setDoc(historyRef, termsData).catch(err => console.warn('Failed to archive historical version terms:', err));
  } catch (err) {
    handleFirestoreError(err, OperationType.WRITE, 'config/legal/termsOfService/current');
  }
}

/**
 * Admin updates Privacy Policy
 */
export async function updatePrivacyPolicy(version: string, effectiveDate: string, content: string): Promise<void> {
  try {
    const privacyRef = doc(db, 'config', 'legal', 'privacyPolicy', 'current');
    const privacyData: LegalDocument = {
      version,
      effectiveDate,
      content,
      updatedAt: Date.now()
    };
    await setDoc(privacyRef, privacyData);

    // Also log historical archive
    const historyRef = doc(db, 'config', 'legal', 'privacyPolicy', `history_${version}`);
    await setDoc(historyRef, privacyData).catch(err => console.warn('Failed to archive historical version privacy:', err));
  } catch (err) {
    handleFirestoreError(err, OperationType.WRITE, 'config/legal/privacyPolicy/current');
  }
}

/**
 * Check a user's acceptance records for terms and privacy
 */
export async function checkUserAcceptances(userId: string): Promise<{ acceptedTermsVersion: string | null; acceptedPrivacyVersion: string | null }> {
  try {
    const termsRef = doc(db, 'users', userId, 'legal', 'acceptedTerms');
    const privacyRef = doc(db, 'users', userId, 'legal', 'acceptedPrivacy');

    const [termsSnap, privacySnap] = await Promise.all([
      getDoc(termsRef),
      getDoc(privacyRef)
    ]);

    return {
      acceptedTermsVersion: termsSnap.exists() ? (termsSnap.data()?.version || null) : null,
      acceptedPrivacyVersion: privacySnap.exists() ? (privacySnap.data()?.version || null) : null
    };
  } catch (err) {
    console.warn('Error reading user acceptances:', err);
    return { acceptedTermsVersion: null, acceptedPrivacyVersion: null };
  }
}

/**
 * Records user acceptance of both legal policies
 */
export async function recordUserAcceptance(
  userId: string, 
  policyType: 'terms' | 'privacy', 
  version: string
): Promise<void> {
  const ip = await fetchClientIp();
  const ipHash = await generateIpHash(ip);
  const path = `users/${userId}/legal/accepted${policyType === 'terms' ? 'Terms' : 'Privacy'}`;

  try {
    const acceptanceRef = doc(db, 'users', userId, 'legal', policyType === 'terms' ? 'acceptedTerms' : 'acceptedPrivacy');
    const data: UserAcceptance = {
      version,
      timestamp: Date.now(),
      ipHash
    };
    await setDoc(acceptanceRef, data);
  } catch (err) {
    handleFirestoreError(err, OperationType.WRITE, path);
  }
}

export const INITIAL_DPA_POLICY = `**eNvesta AI Data Processing Agreement**

Effective Date: June 15, 2026
Version: 1.0

**1. Parties and Scope**

This Data Processing Agreement (“DPA”) forms part of the Terms of Service between eNvesta AI (“Processor”) and the entity identified in the applicable subscription or enterprise agreement (“Controller”). This DPA governs the processing of personal data by eNvesta on behalf of the Controller when the Controller uses eNvesta AI’s venture intelligence platform.

This DPA applies where the Controller is subject to the General Data Protection Regulation (Regulation EU 2016/679), the UK General Data Protection Regulation, the Data Protection and Privacy Act 2019 of Uganda, or equivalent data protection legislation in East African jurisdictions.

**2. Definitions**

**Personal Data** means any information relating to an identified or identifiable natural person processed through the eNvesta AI platform.

**Processing** means any operation performed on Personal Data, whether or not by automated means, including collection, recording, organisation, structuring, storage, adaptation or alteration, retrieval, consultation, use, disclosure by transmission, dissemination or otherwise making available, alignment or combination, restriction, erasure or destruction.

**Data Subject** means the identified or identifiable natural person to whom Personal Data relates.

**Subprocessor** means any third party engaged by eNvesta AI to process Personal Data on behalf of the Controller.

**Security Incident** means any accidental or unlawful destruction, loss, alteration, unauthorised disclosure of, or access to Personal Data transmitted, stored or otherwise processed.

**Standard Contractual Clauses** means the standard contractual clauses for the transfer of personal data to third countries pursuant to Regulation EU 2016/679, as approved by the European Commission.

**3. Roles and Responsibilities**

**3.1 Controller Obligations**

The Controller shall:
- Ensure that all Processing instructions to eNvesta AI comply with applicable data protection law.
- Obtain and maintain all necessary consents, permissions, and lawful bases for the Processing of Personal Data through the eNvesta AI platform.
- Provide accurate and up-to-date information about Data Subjects and ensure that Personal Data shared with eNvesta AI is accurate, complete, and lawful to process.
- Promptly notify eNvesta AI of any Data Subject requests, complaints, or regulatory inquiries relating to Personal Data processed under this DPA.
- Maintain records of Processing activities as required by applicable law.

**3.2 Processor Obligations**

eNvesta AI shall:
- Process Personal Data only on documented instructions from the Controller, including with regard to transfers of Personal Data to third countries or international organisations, unless required to do so by Union or Member State law to which eNvesta AI is subject. In such a case, eNvesta AI shall inform the Controller of that legal requirement before Processing, unless that law prohibits such information on important grounds of public interest.
- Ensure that persons authorised to process the Personal Data have committed themselves to confidentiality or are under an appropriate statutory obligation of confidentiality.
- Implement appropriate technical and organisational measures to ensure a level of security appropriate to the risk, including pseudonymisation and encryption of Personal Data, the ability to ensure the ongoing confidentiality, integrity, availability and resilience of processing systems and services, the ability to restore the availability and access to Personal Data in a timely manner in the event of a physical or technical incident, and a process for regularly testing, assessing and evaluating the effectiveness of technical and organisational measures for ensuring the security of the Processing.
- Not engage another processor without prior specific or general written authorisation of the Controller. In the case of general written authorisation, eNvesta AI shall inform the Controller of any intended changes concerning the addition or replacement of other processors, thereby giving the Controller the opportunity to object to such changes.
- Assist the Controller in ensuring compliance with the obligations pursuant to Articles 32 to 36 of the GDPR or equivalent provisions under other applicable law, taking into account the nature of the Processing and the information available to eNvesta AI.
- At the choice of the Controller, delete or return all Personal Data to the Controller after the end of the provision of services relating to Processing, and delete existing copies unless Union or Member State law requires storage of the Personal Data.
- Make available to the Controller all information necessary to demonstrate compliance with the obligations laid down in Article 28 of the GDPR or equivalent provisions, and allow for and contribute to audits, including inspections, conducted by the Controller or another auditor mandated by the Controller.

**4. Nature and Purpose of Processing**

**4.1 Subject Matter**

eNvesta AI provides venture intelligence services including startup discovery, investor matching, market reconnaissance, and AI-powered insights. The Processing of Personal Data is necessary to deliver these services to the Controller and its authorised users.

**4.2 Duration**

Processing shall continue for the duration of the subscription or enterprise agreement between the Controller and eNvesta AI, plus any period required for data retention under applicable law or for the resolution of disputes.

**4.3 Categories of Data Subjects**

Personal Data processed under this DPA may relate to:
- Founders and entrepreneurs who create profiles or are identified through eNvesta AI’s discovery tools.
- Investors and venture capital professionals who use the platform for deal sourcing and portfolio management.
- Employees and representatives of companies profiled on the platform.
- End users of startups and ventures tracked within the eNvesta AI ecosystem.

**4.4 Types of Personal Data**

eNvesta AI may process the following categories of Personal Data:
- Identity data including names, professional titles, and organisational affiliations.
- Contact data including email addresses, phone numbers, and professional social media profiles.
- Professional data including employment history, investment track records, sector expertise, and educational background.
- Usage data including interaction logs, feature engagement, search queries, and platform navigation patterns.
- Technical data including IP addresses, device identifiers, browser types, and operating systems.
- Financial data relating to subscription payments, billing information, and transaction records processed through Dodo Payments.

**5. Subprocessors**

eNvesta AI engages the following Subprocessors to provide its services. The Controller grants general authorisation for the engagement of these Subprocessors.

**5.1 Current Subprocessors**

- Google Cloud Platform and Firebase provide cloud infrastructure, authentication, database services, and analytics. Data is processed in accordance with Google’s data processing terms and standard contractual clauses. Personal Data may be transferred to and processed in the United States and other jurisdictions where Google maintains data centers.
- Dodo Payments enables international card transactions, Apple Pay, Google Pay, and mobile money (MTN & Airtel). Data is processed in accordance with Dodo Payments’ privacy policy and applicable data protection regulations.
- Gemini AI (Google) powers AI-driven reconnaissance and insights generation. Data is processed in accordance with Google’s AI data governance terms. Input data may be processed in the United States and other jurisdictions.

**5.2 Changes to Subprocessors**

eNvesta AI shall maintain an up-to-date list of Subprocessors at eNvestaai.com/subprocessors. eNvesta AI shall notify the Controller of any intended addition or replacement of Subprocessors at least thirty days in advance by updating this list and sending notice to the Controller’s designated contact. The Controller may object to a new Subprocessor on reasonable grounds related to data protection. If the Controller objects, the parties shall discuss the objection in good faith. If no resolution is reached within fourteen days, the Controller may terminate the affected services without penalty.

**6. Data Security**

**6.1 Technical Measures**

eNvesta AI implements the following technical security measures:
- Encryption of Personal Data in transit using Transport Layer Security version 1.2 or higher.
- Encryption of Personal Data at rest using industry-standard encryption algorithms.
- Multi-factor authentication for administrative access to production systems.
- Role-based access controls limiting data access to authorised personnel.
- Regular automated security scanning and vulnerability assessments.
- Secure software development practices including code review and dependency auditing.

**6.2 Organisational Measures**

eNvesta AI implements the following organisational security measures:
- Data protection training for all personnel with access to Personal Data.
- Written confidentiality agreements with all employees and contractors.
- Incident response procedures for detecting, reporting, and mitigating Security Incidents.
- Business continuity and disaster recovery plans tested at least annually.
- Regular review and update of security policies and procedures.

**6.3 Data Minimisation**

eNvesta AI processes only the Personal Data necessary to provide its services. AI-generated insights are derived from aggregated and anonymised datasets where possible. Individual-level data is retained only for operational purposes and deleted when no longer necessary.

**7. International Data Transfers**

**7.1 Transfer Mechanisms**

Personal Data may be transferred from Uganda and East Africa to the United States, the United Kingdom, the European Union, and other jurisdictions where Subprocessors operate. eNvesta AI ensures that such transfers are subject to appropriate safeguards, including:
- Standard Contractual Clauses approved by the European Commission for transfers to Subprocessors in third countries.
- Adequacy decisions by the European Commission where applicable.
- Binding corporate rules or equivalent intra-group transfer mechanisms where applicable.

**7.2 Transfer Impact Assessments**

eNvesta AI conducts transfer impact assessments for jurisdictions that do not benefit from an adequacy decision. These assessments evaluate the law and practice of the destination country, the security measures in place, and any supplementary measures necessary to ensure an essentially equivalent level of protection.

**7.3 Notification of Changes**

eNvesta AI shall notify the Controller of any changes to the jurisdictions in which Personal Data is processed or stored, or any changes to the transfer mechanisms relied upon.

**8. Security Incidents and Breach Notification**

**8.1 Detection and Response**

eNvesta AI maintains systems and procedures to detect Security Incidents. Upon becoming aware of a Security Incident, eNvesta AI shall:
- Take immediate steps to contain and mitigate the incident.
- Investigate the incident to determine its scope, cause, and impact.
- Notify the Controller without undue delay and in any event within seventy-two hours of becoming aware of the incident, unless the incident is unlikely to result in a risk to the rights and freedoms of natural persons.

**8.2 Notification Content**

eNvesta AI’s notification of a Security Incident shall include:
- A description of the nature of the incident including the categories and approximate number of Data Subjects concerned and the categories and approximate number of Personal Data records concerned.
- The name and contact details of the data protection officer or other contact point where more information can be obtained.
- A description of the likely consequences of the incident.
- A description of the measures taken or proposed to be taken by eNvesta AI to address the incident, including measures to mitigate its possible adverse effects.

**8.3 Cooperation**

eNvesta AI shall cooperate with the Controller in investigating and responding to Security Incidents, including providing assistance with notifying Data Subjects and supervisory authorities where required by applicable law.

**9. Data Subject Rights**

**9.1 Assistance with Requests**

eNvesta AI shall assist the Controller in responding to Data Subject requests to exercise their rights under applicable data protection law, including the rights of access, rectification, erasure, restriction of Processing, data portability, and objection.

**9.2 Response Timeframes**

eNvesta AI shall respond to the Controller’s requests for assistance within five business days. Where a Data Subject request is received directly by eNvesta AI, eNvesta AI shall forward it to the Controller within two business days and shall not respond to the Data Subject directly unless authorised by the Controller.

**9.3 Technical Assistance**

eNvesta AI shall provide technical functionality to enable the Controller to fulfil Data Subject requests, including export and deletion capabilities accessible through the platform interface.

**10. Audit and Inspection**

**10.1 Right to Audit**

The Controller has the right to audit eNvesta AI’s compliance with this DPA. Audits may be conducted by the Controller or by an independent auditor appointed by the Controller, subject to reasonable confidentiality obligations.

**10.2 Audit Frequency and Scope**

The Controller may conduct one audit per calendar year at no cost. Additional audits may be conducted where the Controller has reasonable grounds to believe that eNvesta AI is not complying with this DPA, subject to eNvesta AI’s reasonable reimbursement of costs.

**10.3 Audit Notice**

The Controller shall provide at least thirty days’ advance notice of an audit. Audits shall be conducted during normal business hours and shall not unreasonably disrupt eNvesta AI’s operations.

**10.4 Audit Reports**

eNvesta AI shall provide the Controller with relevant documentation and records necessary to demonstrate compliance with this DPA, including records of Processing activities, security assessments, and Subprocessor agreements.

**11. Data Return and Deletion**

**11.1 End of Service**

Upon termination or expiry of the subscription or enterprise agreement, eNvesta AI shall, at the Controller’s election, return or delete all Personal Data processed on behalf of the Controller.

**11.2 Return Format**

Where the Controller elects return, eNvesta AI shall provide Personal Data in a structured, commonly used, and machine-readable format, such as JSON or CSV.

**11.3 Retention Exceptions**

eNvesta AI may retain Personal Data where required by applicable law, for the establishment, exercise, or defence of legal claims, or as necessary for fraud prevention. Any retained data shall be subject to the confidentiality and security obligations of this DPA.

**11.4 Certification of Deletion**

Upon request, eNvesta AI shall provide the Controller with written certification that Personal Data has been deleted in accordance with this Section.

**12. Liability and Indemnification**

**12.1 Processor Liability**

eNvesta AI’s liability for breaches of this DPA shall be subject to the limitations set out in the Terms of Service, except where such limitations are prohibited by applicable data protection law.

**12.2 Indemnification**

eNvesta AI shall indemnify the Controller against any claims, damages, fines, and penalties imposed on the Controller by a supervisory authority or court as a result of eNvesta AI’s breach of this DPA, except to the extent that eNvesta AI’s liability is excluded under the Terms of Service or applicable law.

**12.3 Controller Liability**

The Controller shall indemnify eNvesta AI against any claims arising from the Controller’s failure to comply with its obligations under this DPA, including providing unlawful instructions or failing to obtain necessary consents.

**13. Term and Termination**

**13.1 Term**

This DPA shall commence on the Effective Date and shall continue for the duration of the subscription or enterprise agreement between the parties.

**13.2 Survival**

Sections relating to confidentiality, data security, audit, data return and deletion, liability, and dispute resolution shall survive termination of this DPA.

**13.3 Termination for Breach**

Either party may terminate this DPA if the other party materially breaches its obligations and fails to remedy the breach within thirty days of written notice.

**14. Governing Law and Dispute Resolution**

**14.1 Governing Law**

This DPA shall be governed by and construed in accordance with the laws of the Republic of Uganda, without regard to its conflict of law provisions. Where the Controller is subject to the GDPR or UK GDPR, the applicable provisions of those regulations shall prevail in the event of any conflict.

**14.2 Jurisdiction**

The courts of Uganda shall have exclusive jurisdiction over any dispute arising from this DPA, except where the Controller is a consumer resident in the European Economic Area or the United Kingdom, in which case the courts of the Controller’s place of residence shall have jurisdiction.

**14.3 Dispute Resolution**

The parties shall attempt to resolve any dispute arising from this DPA through good faith negotiation. If negotiation fails, the parties may agree to mediation or arbitration in Kampala, Uganda, under the rules of the Centre for Arbitration and Dispute Resolution.

**15. Amendments**

eNvesta AI may amend this DPA to reflect changes in applicable law, technology, or business practices. Material changes shall be notified to the Controller at least thirty days in advance. Continued use of the platform after the effective date of amendments constitutes acceptance.

**16. Contact Information**

For questions about this DPA or to exercise your rights:
- Data Protection Officer: dpo@eNvestaai.com
- Privacy Team: privacy@eNvestaai.com
- Business Address: [Your registered business address]
- Response Time: Seventy-two hours

**17. Annex A: Processing Details**

- Subject Matter: Provision of venture intelligence platform services
- Duration: As per subscription or enterprise agreement
- Nature and Purpose: Collection, storage, analysis, and presentation of venture and market intelligence data
- Categories of Data Subjects: Founders, investors, employees, end users
- Types of Personal Data: Identity, contact, professional, usage, technical, financial
- Special Categories: None, unless explicitly provided by the Controller
- Subprocessors: Google Cloud Platform, Firebase, Dodo Payments, Gemini AI

By signing the applicable subscription or enterprise agreement, or by continuing to use eNvesta AI under an enterprise arrangement, the Controller agrees to the terms of this Data Processing Agreement.

eNvesta AI is a venture intelligence platform serving global markets. Built with Firebase, Gemini AI, and designed for founders, investors, and ecosystem builders.`;
